import React from 'react'
import cls from 'classnames'
import styled, { css } from 'styled-components'

export const STYLE_TYPES = {
  NEUTRAL: 'neutral',
  EMPHASIS: 'emphasis',
}

const variantStyles = {
  [STYLE_TYPES.NEUTRAL]: css`
    color: var(--sub-text-color);

    &:not(:disabled):hover,
    &:not(:disabled).__hover,
    &:not(:disabled):active {
      background: var(--button-tertiary-bg-hover);
      color: var(--button-fourth-text-hover);
    }
  `,
  [STYLE_TYPES.EMPHASIS]: css`
    color: var(--button-fourth-text-default);

    &:not(:disabled):hover,
    &:not(:disabled).__hover,
    &:not(:disabled):active {
      background: var(--button-fourth-bg-hover);
      color: var(--button-fourth-text-hover);
    }
  `,
}

const StyledActionIcon = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  background: transparent;
  border: 0;
  color: var(--sub-text-color);
  transition:
    background 0.2s ease,
    color 0.2s ease;
  width: 24px;
  height: 24px;

  /* Keyboard focus indicator (WCAG 2.4.7) matching the DS Button ring; only
     shows for keyboard focus, never on mouse/touch. */
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  & > div {
    width: ${({ width }) => width || '20px'};
    height: ${({ height }) => height || '20px'};
  }

  svg > path {
    fill: currentColor !important;
  }

  ${({ variant }) => variantStyles[variant]}

  &:disabled {
    cursor: default;
    color: var(--button-tertiary-text-disabled);
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  ${({ active, variant }) =>
    active &&
    css`
      background: ${
        variant === STYLE_TYPES.EMPHASIS
          ? 'var(--button-fourth-bg-default)'
          : 'var(--button-tertiary-bg-hover)'
      };
      color: ${
        variant === STYLE_TYPES.EMPHASIS
          ? 'var(--button-fourth-text-default)'
          : 'var(--button-fourth-text-hover)'
      };
    `}
`

/**
 * @typedef {Object} ActionIconStyledProps
 * @property {string} id - Required unique identifier for the icon component.
 * @property {string} [className] - Optional CSS class name for custom styling.
 * @property {string} dataTestId -  Unique identifier for test automation (data-testid).
 * @property {string} [title] - Accessible label or tooltip text shown on hover.
 * @property {() => void} [onClick] - Callback function triggered on click.
 * @property {boolean} [disabled] - Whether the icon is disabled.
 * @property {boolean} [isLink] - Whether the component should be rendered as a link.
 * @property {boolean} [active] - Whether the icon is currently active/selected.
 * @property {React.ElementType} Icon - Required React component for the icon.
 * @property {keyof typeof STYLE_TYPES} [styleType] - Style type from STYLE_TYPES enum.
 * @property {Object} [iconProps] - Additional props to pass to the icon component.
 * @property {Object} [containerStyle] - Inline styles for the icon container.
 * @property {string} [href] - Link URL if the icon is rendered as a link (`isLink` is true).
 * @property {string} [dataPendoFeature] - Stable Pendo Feature value (`data-pendo-feature` on the button).
 * @property {string} [rel] - Specifies the relationship between the current document and the linked URL (used when `isLink` is true, defaults to "noopener noreferrer").
 */
const ActionIcon = ({
  id,
  disabled,
  onClick,
  active,
  Icon = <></>,
  iconProps,
  styleType = STYLE_TYPES.NEUTRAL,
  className,
  title,
  containerStyle = {},
  isLink = false,
  href,
  rel = 'noopener noreferrer',
  dataTestId = id || '',
  target = '_blank',
  dataPendoFeature,
  ariaLabel,
}) => (
  <StyledActionIcon
    type="button"
    id={id}
    active={active}
    data-testid={dataTestId}
    data-pendo-feature={dataPendoFeature}
    aria-label={ariaLabel}
    onClick={onClick}
    disabled={disabled}
    variant={styleType}
    className={cls(className)}
    style={{
      ...containerStyle,
    }}
    title={title}
    height={iconProps?.height}
    width={iconProps?.width}
    {...((isLink || href) && !disabled && { as: 'a', href, rel, target })}
  >
    <Icon {...iconProps} />
  </StyledActionIcon>
)

ActionIcon.STYLE_TYPES = STYLE_TYPES

export default ActionIcon
